import {
  Color,
  Mesh,
  NormalBlending,
  PlaneGeometry,
  ShaderMaterial,
  Vector2,
} from 'three';
import { Metrics } from '../_app/cuchillo/core/Metrics';

// Núvols del cel: un pla gran, davant i una mica per sobre de l'horitzó, amb un shader de value-noise
// (fBm) que dibuixa formes de núvol suaus i semitransparents que deriven lentament amb el temps.
// És autònom (no depèn de l'hora); Scene el munta al SEA_LAYER (render de dalt). Segueix el
// mateix cicle de vida que CelestialBody/StarField: init/loop/setConfig/setLayer/dispose/settings.
//
// camera2D: la càmera mira horitzontal cap a -Z; col·loquem el pla a z=-distance (de cara a la
// càmera) i el pugem a `height` perquè els núvols quedin per sobre de l'horitzó (centre de pantalla).
export const CLOUD_SETTINGS = {
  color: '#010845',   // color dels núvols
  coverage: 0.36,     // 0..1 quantitat de núvol (llindar; ↑ = més cel tapat)
  softness: 0.54,     // suavitat de les vores dels núvols
  opacity: 0.70,      // opacitat global
  speed: 0.014,         // velocitat de deriva
  dirX: .65,          // direcció de deriva X
  dirY: -.05,         // direcció de deriva Y
  scale: 3.1,         // mida dels núvols (↑ = més petits i detallats)
  band: 0.42,         // amplada vertical de la franja de núvols (fracció del pla; s'esvaeix a dalt/baix)
  // RESPONSIVE: mides/posicions en FRACCIÓ de pantalla × Metrics.WIDTH/HEIGHT al punt d'ús (ref
  // 1440×900). SCALE = × WIDTH (amplada/distància); VERT = × HEIGHT (alçada/posició vertical).
  height: 1.444444,    // VERT  · ≈1300px @900 · posició vertical del centre (a l'alçada del sol)
  sizeX: 9.722222,     // SCALE · ≈14000px @1440 · amplada del pla
  sizeY: 4.0,          // VERT  · ≈3600px @900 · alçada del pla
  distance: 2.083333,  // SCALE · ≈3000px @1440 · distància davant la càmera
};

const VERTEX = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  uniform vec3 uColor;
  uniform float uTime, uCoverage, uSoftness, uOpacity, uScale, uBand;
  uniform vec2 uDrift; // direcció × velocitat (baked)

  // Value-noise suau (interpolació smootherstep dels hashos de les cantonades) + fBm de 5 octaves.
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = hash(i), b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0)), d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }
  float fbm(vec2 p) {
    float v = 0.0, amp = 0.5;
    for (int i = 0; i < 5; i++) { v += amp * vnoise(p); p *= 2.02; amp *= 0.5; }
    return v;
  }

  void main() {
    // Domini del noise: aspecte 2:1 (núvols amples) × escala + deriva temporal.
    vec2 p = vec2(vUv.x * 2.0, vUv.y) * (uScale * 3.0) + uDrift * uTime;
    float n = fbm(p);
    // Llindar de cobertura → densitat de núvol amb vores suaus.
    float th = 1.0 - uCoverage;
    float cloud = smoothstep(th - uSoftness, th + uSoftness, n);
    // Franja vertical: núvols concentrats al centre del pla, s'esvaeixen a dalt i a baix.
    float band = smoothstep(0.0, uBand, vUv.y) * smoothstep(1.0, 1.0 - uBand, vUv.y);
    float a = cloud * band * uOpacity;
    if (a <= 0.003) discard;
    gl_FragColor = vec4(uColor, a);
  }
`;

export default class Clouds {
  constructor(opts = {}) {
    if (opts.settings) Object.assign(CLOUD_SETTINGS, opts.settings);
    this.mesh = null;
    this.material = null;
  }

  init() {
    const s = CLOUD_SETTINGS;
    this.material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new Color(s.color) },
        uCoverage: { value: s.coverage },
        uSoftness: { value: s.softness },
        uOpacity: { value: s.opacity },
        uScale: { value: s.scale },
        uBand: { value: s.band },
        uDrift: { value: new Vector2(s.dirX * s.speed, s.dirY * s.speed) },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: NormalBlending, // núvols per sobre del cel (no additius)
    });
    const W = Metrics.WIDTH, H = Metrics.HEIGHT;
    this.mesh = new Mesh(new PlaneGeometry(s.sizeX * W, s.sizeY * H), this.material);
    this.mesh.position.set(0, s.height * H, -s.distance * W); // fraccions × pantalla (responsive)
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1; // sobre la volta del cel, sota sol/lluna/estrelles
    return this.mesh;
  }

  loop(dt = 0.016) {
    if (this.material) this.material.uniforms.uTime.value += dt;
  }

  setConfig(partial = {}) {
    Object.assign(CLOUD_SETTINGS, partial);
    const s = CLOUD_SETTINGS;
    if (this.material) {
      const u = this.material.uniforms;
      u.uColor.value.set(s.color);
      u.uCoverage.value = s.coverage;
      u.uSoftness.value = s.softness;
      u.uOpacity.value = s.opacity;
      u.uScale.value = s.scale;
      u.uBand.value = s.band;
      u.uDrift.value.set(s.dirX * s.speed, s.dirY * s.speed);
    }
    this._applySize();
  }

  setLayer(n) { if (this.mesh) this.mesh.layers.set(n); }

  // Ara SÍ depèn de la mida de pantalla: reconstrueix el pla i reposiciona (fraccions × Metrics).
  resize() { this._applySize(); }

  _applySize() {
    if (!this.mesh) return;
    const s = CLOUD_SETTINGS, W = Metrics.WIDTH, H = Metrics.HEIGHT;
    this.mesh.geometry.dispose();
    this.mesh.geometry = new PlaneGeometry(s.sizeX * W, s.sizeY * H);
    this.mesh.position.set(0, s.height * H, -s.distance * W);
  }

  dispose() {
    this.mesh?.geometry?.dispose();
    this.material?.dispose();
    this.mesh = null;
    this.material = null;
  }

  get settings() { return CLOUD_SETTINGS; }
}
